import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('rs_token');
    if (token) {
      api.get('/auth/me')
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem('rs_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for forced logout from api interceptor
  useEffect(() => {
    const handleForceLogout = () => {
      localStorage.removeItem('rs_token');
      localStorage.removeItem('rs_refresh_token');
      setUser(null);
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('rs_token', res.data.token);
    if (res.data.refreshToken) localStorage.setItem('rs_refresh_token', res.data.refreshToken);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (first_name, last_name, email, password) => {
    const res = await api.post('/auth/register', { first_name, last_name, email, password });
    localStorage.setItem('rs_token', res.data.token);
    if (res.data.refreshToken) localStorage.setItem('rs_refresh_token', res.data.refreshToken);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('rs_refresh_token');
    // Invalidate refresh token server-side (best-effort)
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => {});
    }
    localStorage.removeItem('rs_token');
    localStorage.removeItem('rs_refresh_token');
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await api.put('/auth/profile', data);
    setUser(prev => ({ ...prev, ...data }));
    return res.data;
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch { /* ignore */ }
  };

  // ── Trial helpers ──
  const trialInfo = useMemo(() => {
    if (!user) return { isFree: false, isExpired: false, hoursLeft: 0 };
    const plan = user.subscription_plan || 'free';
    const isFree = plan === 'free';
    if (!isFree) return { isFree: false, isExpired: false, hoursLeft: null };

    // If created_at is missing, treat trial as expired to be safe
    if (!user.created_at) return { isFree: true, isExpired: true, hoursLeft: 0 };

    const createdAt = new Date(user.created_at);
    if (isNaN(createdAt.getTime())) return { isFree: true, isExpired: true, hoursLeft: 0 };

    const now = new Date();
    const hoursElapsed = (now - createdAt) / (1000 * 60 * 60);
    const totalTrialHours = 14 * 24; // 14 days
    const hoursLeft = Math.max(0, totalTrialHours - hoursElapsed);
    const daysLeft = Math.ceil(hoursLeft / 24);
    const isExpired = hoursLeft <= 0;

    return { isFree: true, isExpired, hoursLeft: Math.round(hoursLeft), daysLeft };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, refreshUser, trialInfo }}>
      {children}
    </AuthContext.Provider>
  );
};
